import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Loader2, Facebook, ShieldCheck, Instagram, ChevronRight } from 'lucide-react';
import api from '../lib/api';

const FB_SDK_ID = 'facebook-jssdk';

/**
 * Meta (Facebook + Instagram) one-click connect via Facebook Login for Business.
 * Mirrors WhatsApp Embedded Signup: FB.login → code → backend lists Pages →
 * user picks a Page → backend stores token + subscribes leadgen/messages webhooks.
 */
export const MetaEmbeddedSignup = ({ onConnected }) => {
  const [config, setConfig] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [pages, setPages] = useState(null);   // null = no picker, [] = none found
  const [connectingId, setConnectingId] = useState('');

  useEffect(() => {
    let active = true;
    api.get('/api/social-connectors/meta/oauth-config')
      .then((res) => { if (active) setConfig(res.data || null); })
      .catch(() => { if (active) setConfig({ enabled: false }); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!config?.enabled || !config.app_id) return;
    const init = () => {
      if (!window.FB) return;
      window.FB.init({ appId: config.app_id, cookie: true, xfbml: false, version: config.graph_version || 'v23.0' });
      setSdkReady(true);
    };
    if (window.FB) { init(); return; }
    window.fbAsyncInit = init;
    if (!document.getElementById(FB_SDK_ID)) {
      const js = document.createElement('script');
      js.id = FB_SDK_ID; js.src = 'https://connect.facebook.net/en_US/sdk.js';
      js.async = true; js.defer = true; js.crossOrigin = 'anonymous';
      document.body.appendChild(js);
    }
  }, [config]);

  const loadPages = useCallback(async (code) => {
    try {
      const res = await api.post('/api/social-connectors/meta/oauth/pages', { code });
      setPages(res.data?.pages || []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error('Could not load Pages', { description: typeof detail === 'string' ? detail : 'Try connecting again' });
    } finally {
      setLaunching(false);
    }
  }, []);

  const fbLoginCallback = useCallback((response) => {
    if (response?.authResponse?.code) {
      loadPages(response.authResponse.code);
    } else {
      setLaunching(false);
      toast.info('Facebook connect was cancelled');
    }
  }, [loadPages]);

  const launch = () => {
    if (!window.FB || !config?.config_id) { toast.error('Facebook SDK not ready yet'); return; }
    setLaunching(true);
    window.FB.login(fbLoginCallback, {
      config_id: config.config_id,
      response_type: 'code',
      override_default_response_type: true,
    });
  };

  const connectPage = async (pageId) => {
    setConnectingId(pageId);
    try {
      const res = await api.post('/api/social-connectors/meta/oauth/connect', { page_id: pageId });
      toast.success('Connected', { description: res.data?.detail || res.data?.page_name });
      setPages(null);
      onConnected && onConnected();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error('Connect failed', { description: typeof detail === 'string' ? detail : 'Could not connect Page' });
    } finally {
      setConnectingId('');
    }
  };

  if (config && !config.enabled) return null;

  return (
    <div className="space-y-2" data-testid="meta-embedded-signup">
      <Button type="button" onClick={launch} disabled={!sdkReady || launching}
        className="w-full bg-[#1877f2] hover:bg-[#166fe0] text-white" data-testid="meta-es-launch-btn">
        {launching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Facebook className="h-4 w-4 mr-2" />}
        {sdkReady ? 'Connect with Facebook' : 'Loading…'}
      </Button>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-green-500 shrink-0" />
        <span>Recommended — connect your Facebook Page & Instagram in one click. Lead Ads and DMs flow in automatically.</span>
      </div>

      <Dialog open={pages !== null} onOpenChange={(o) => { if (!o && !connectingId) setPages(null); }}>
        <DialogContent className="max-w-md" data-testid="meta-page-picker">
          <DialogHeader>
            <DialogTitle>Choose a Page to connect</DialogTitle>
            <DialogDescription>Select the Facebook Page whose Lead Ads & messages should flow into Lockene.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {pages && pages.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">No Facebook Pages found for this account.</p>
            )}
            {(pages || []).map((p) => (
              <button key={p.id} onClick={() => connectPage(p.id)} disabled={!!connectingId}
                className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-left disabled:opacity-50"
                data-testid={`meta-page-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  {p.instagram && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-pink-500"><Instagram className="h-3 w-3" />{p.instagram_username || 'Instagram linked'}</span>
                  )}
                </div>
                {connectingId === p.id ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetaEmbeddedSignup;
